import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import LegalDisclaimer from '../legal/LegalDisclaimer';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { 
  Users, 
  CreditCard, 
  Eye, 
  Globe, 
  Link as LinkIcon, 
  RotateCcw,
  Database,
  Wallet,
  ChevronRight,
  Play,
  Star,
  Shield,
  TrendingUp,
  Gift
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const [prospectData, setProspectData] = useState({
    full_name: '',
    email: '',
    voucher_code: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProspectData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLeadCapture = async (e) => {
    e.preventDefault();
    
    if (!prospectData.full_name || !prospectData.email || !prospectData.phone) {
      toast.error('Please enter your name, email, and phone number');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/pif/prospects`, prospectData);
      
      toast.success(response.data.message);
      
      // Clear form
      setProspectData({
        full_name: '',
        email: '',
        voucher_code: '',
        phone: ''
      });
      
    } catch (error) {
      console.error('Failed to submit prospect:', error);
      const message = error.response?.data?.detail || 'Failed to join PIF list';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    {
      icon: CreditCard,
      title: "Banner Ads",
      description: "Create and display banner advertisements across the network"
    },
    {
      icon: Database,
      title: "Text Ads",
      description: "Manage text-based advertising campaigns with rotation"
    },
    {
      icon: Eye,
      title: "Traffic Views",
      description: "Generate targeted traffic views with 30-second timer"
    },
    {
      icon: Globe,
      title: "Splash/Landing Pages",
      description: "Build professional landing pages with WYSIWYG editor"
    },
    {
      icon: LinkIcon,
      title: "Link-in-Bio",
      description: "Create custom link-in-bio pages with click analytics"
    },
    {
      icon: RotateCcw,
      title: "URL Rotator & Split Test",
      description: "Test and optimize with weighted link rotation"
    },
    {
      icon: Database,
      title: "CRM Lite",
      description: "Manage contacts, tags, and follow-up sequences"
    },
    {
      icon: Wallet,
      title: "Peer-to-Peer Payment System",
      description: "We do not collect any funds for members. Non-custodial member-to-member payments with auto payment confirmation and product release."
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Digital Marketer",
      content: "BBC Quick List transformed my advertising approach. The spillover system actually works!",
      rating: 5
    },
    {
      name: "Mike Chen",
      role: "Entrepreneur",
      content: "Finally, a platform where small wins stack into big ones. The tools are professional grade.",
      rating: 5
    },
    {
      name: "Lisa Rodriguez",
      role: "Affiliate Marketer",
      content: "The compensation system is transparent and fair. I've seen real results in just 30 days.",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center font-bold text-black">
                BBC
              </div>
              <div className="ml-3">
                <div className="font-bold text-xl text-gray-900">Quick List</div>
                <div className="text-xs text-gray-500">Professional Marketing Platform</div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/login">
                <Button variant="ghost">Sign In</Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <h1 className="text-4xl sm:text-6xl font-bold text-gray-900 mb-6">
              Professional Marketing
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
                Made Simple
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              BBC Quick List provides comprehensive advertising tools and credits system. 
              Build your marketing presence with our professional-grade platform where 
              tools come first, spillover is real, and small wins stack into big ones.
            </p>
            
            {/* Enhanced PIF Lead Capture Form */}
            <div className="max-w-lg mx-auto mb-8">
              <div className="bg-white rounded-lg shadow-lg p-6 border-2 border-yellow-300">
                <div className="flex items-center justify-center mb-4">
                  <Gift className="h-6 w-6 text-yellow-600 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Join Our Pay It Forward List
                  </h3>
                </div>
                
                {/* Invitation Only Notice - MOVED BEFORE description */}
                <div className="bg-blue-50 border-2 border-blue-400 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-900 font-semibold text-center">
                    🔒 <strong>Invitation Only Platform</strong>
                  </p>
                  <p className="text-xs text-blue-800 text-center mt-2">
                    Request a voucher code below or get referred by an existing member to join.
                  </p>
                </div>
                
                <p className="text-sm text-gray-600 text-center mb-4">
                  Get on our exclusive list! Current members can sponsor your account with $10 free credits.
                </p>
                
                <form onSubmit={handleLeadCapture} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="full_name" className="text-sm">Full Name</Label>
                      <Input
                        id="full_name"
                        name="full_name"
                        type="text"
                        placeholder="Your full name"
                        value={prospectData.full_name}
                        onChange={handleInputChange}
                        required
                        disabled={isSubmitting}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-sm">Email Address</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="your@email.com"
                        value={prospectData.email}
                        onChange={handleInputChange}
                        required
                        disabled={isSubmitting}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="phone" className="text-sm">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={prospectData.phone}
                        onChange={handleInputChange}
                        required
                        disabled={isSubmitting}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="voucher_code" className="text-sm">Voucher Code (Optional)</Label>
                      <Input
                        id="voucher_code"
                        name="voucher_code"
                        type="text"
                        placeholder="Enter if you have one"
                        value={prospectData.voucher_code}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full btn-primary mt-4"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Joining PIF List...
                      </div>
                    ) : (
                      <>
                        <Gift className="h-4 w-4 mr-2" />
                        Join Pay It Forward List
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register">
                <Button size="lg" className="btn-primary">
                  Request a Voucher Code
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Button variant="outline" size="lg" className="flex items-center">
                <Play className="mr-2 h-5 w-5" />
                Watch Demo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Video Placeholder */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-800 rounded-xl aspect-video flex items-center justify-center">
            <div className="text-center text-white">
              <Play className="h-16 w-16 mx-auto mb-4 text-yellow-400" />
              <h3 className="text-xl font-semibold mb-2">Platform Overview Video</h3>
              <p className="text-gray-300">See how BBC Quick List can transform your marketing</p>
            </div>
          </div>
        </div>
      </section>

      {/* Need More Information - FAQ Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
            Need More Information?
          </h3>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Get answers to all your questions about BBC Quick List, our compensation plan, 
            payment system, and platform features.
          </p>
          
          <div className="flex justify-center items-center">
            <Link to="/faq">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-black font-bold text-lg px-12 py-4 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 inline-flex items-center justify-center"
              >
                Frequently Asked Questions
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
          
          <p className="text-sm text-gray-500 mt-4">
            Over 165 detailed answers covering everything you need to know
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Professional Marketing Tools
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to build, promote, and scale your online presence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Icon className="h-6 w-6 text-black" />
                    </div>
                    <CardTitle className="text-lg">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pay It Forward Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-yellow-50 to-orange-50">
        <div className="max-w-4xl mx-auto text-center">
          <Gift className="h-16 w-16 mx-auto mb-6 text-yellow-600" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Pay It Forward System
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Current members can sponsor new prospects with $10 welcome credits. 
            Join our PIF list above and get sponsored by an existing member!
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold">1</span>
              </div>
              <h3 className="font-semibold mb-2">Join PIF List</h3>
              <p className="text-sm text-gray-600">Add your name and email to our Pay It Forward prospect list</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold">2</span>
              </div>
              <h3 className="font-semibold mb-2">Get Sponsored</h3>
              <p className="text-sm text-gray-600">A current member pays $10 forward to sponsor your account</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold">3</span>
              </div>
              <h3 className="font-semibold mb-2">Start with $10</h3>
              <p className="text-sm text-gray-600">Register with your voucher and get $10 welcome credits instantly</p>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Message */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8">Message from the Founder</h2>
          <div className="bg-gray-800 rounded-xl p-8">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                <Play className="h-8 w-8 text-black" />
              </div>
            </div>
            <blockquote className="text-xl italic text-gray-300 mb-6">
              "I built BBC because I was tired of systems where only the top 1% win. 
              Here, tools come first, spillover is real, and small wins can stack into big ones. 
              This is serious business for serious people."
            </blockquote>
            <p className="text-yellow-400 font-semibold">- RD, Founder</p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              What Our Members Say
            </h2>
            <p className="text-xl text-gray-600">
              Real results from real people using BBC Quick List
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200">
                <CardHeader>
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <CardDescription className="text-gray-700 text-base italic">
                    "{testimonial.content}"
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.role}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-yellow-400 to-orange-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-black mb-6">
            Ready to Transform Your Marketing?
          </h2>
          <p className="text-xl text-black/80 mb-8">
            Join thousands of successful marketers using BBC Quick List
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-black text-white hover:bg-gray-800">
                Start Your Free Account
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <div className="flex items-center text-black/80">
              <Shield className="h-5 w-5 mr-2" />
              <span>No credit card required</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center font-bold text-black">
                  BBC
                </div>
                <span className="ml-2 font-bold">Quick List</span>
              </div>
              <p className="text-gray-400 text-sm">
                Professional marketing platform for serious business builders.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Platform</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-white">Features</a></li>
                <li><a href="#" className="hover:text-white">Pricing</a></li>
                <li><a href="#" className="hover:text-white">Tools</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/faq" className="hover:text-white">Help Center</a></li>
                <li><a href="mailto:support@bbcquicklist.com" className="hover:text-white">Contact</a></li>
                <li><a href="/member/training-vault" className="hover:text-white">Training</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/privacy-policy" className="hover:text-white">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-white">Terms & Conditions</a></li>
                <li><a href="/faq" className="hover:text-white">FAQ</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2024 BBC Quick List. All rights reserved.</p>
            <p className="mt-2">
              <strong>Earnings Disclaimer:</strong> Results vary and are not guaranteed. 
              Success depends on your effort, participation, and market conditions.
            </p>
            <div className="mt-4 flex justify-center gap-6 text-xs">
              <a href="/login" className="hover:text-white transition-colors">Member Login</a>
              <span className="text-gray-600">•</span>
              <a href="/admin/login" className="hover:text-white transition-colors">Admin</a>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Legal Disclaimer */}
      <LegalDisclaimer type="homepage" />
    </div>
  );
};

export default LandingPage;